import { create } from 'zustand'
import type { User as ModdyUser } from '../types/User'
import type { User as FirebaseUser } from 'firebase/auth'
import type { DecodedToken } from '../types/Auth'
import {
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    sendEmailVerification,
    sendPasswordResetEmail,
} from 'firebase/auth'
import { jwtDecode } from 'jwt-decode'
import { API_CLIENT_URLS } from '../services/api/client'
import { tokenService } from '../services/tokenService'
import { getFirebaseAuth } from '../services/firebaseService'
import { getDeviceId, getSessionId, clearSession } from '../utils/userSessions.ts'
const API_URL = API_CLIENT_URLS.identityApiUrl

const ALLOWED_DOMAIN = 'moddy.ai'
const EMAIL_EXCEPTIONS = ['todd.rogers@gmail.com']

const isEmailAllowedForEnvironment = (email: string): boolean => {
    const stage = import.meta.env.VITE_APP_API_STAGE || 'prod'
    if (stage === 'prod') return true
    const normalizedEmail = email.toLowerCase().trim()
    if (EMAIL_EXCEPTIONS.includes(normalizedEmail)) return true
    return normalizedEmail.endsWith(`@${ALLOWED_DOMAIN}`)
}

export const RESTRICTED_ENV_MESSAGE = 'This environment is restricted.'

// Get auth instance from FirebaseService
const auth = getFirebaseAuth()

// Custom error for signup capacity limits
export class AtCapacityError extends Error {
    code = 'at_capacity'
    constructor(message = 'We are currently at capacity for new accounts.') {
        super(message)
        this.name = 'AtCapacityError'
    }
}

// Result type for signup flow
export type SignUpResult =
    | { success: true }
    | { success: false; error: 'at_capacity' | 'firebase' | 'unknown'; message: string }

// Profile data collected during email/password signup
export interface SignUpProfileData {
    name: string
    socials?: {
        twitterHandle?: string
        instagramHandle?: string
        tiktokHandle?: string
        facebookHandle?: string
        youtubeHandle?: string
    }
}

// Flag to prevent auth listener from doing token exchange during signup/signin flows
let tokenExchangeInProgress = false

interface UserState {
    user: ModdyUser | null
    firebaseUser: FirebaseUser | null
    token: string | null
    loading: boolean
    error: Error | null
    ejectionStatus: string | null
    signIn: (email: string, password: string) => Promise<void>
    signInWithGoogle: () => Promise<SignUpResult>
    signOut: () => Promise<void>
    signUp: (email: string, password: string, profileData?: SignUpProfileData) => Promise<SignUpResult>
    sendPasswordResetEmail: (email: string) => Promise<void>
    getValidToken: (forceRefresh?: boolean) => Promise<string | null>
    setFirebaseUser: (user: FirebaseUser | null) => void
    setUser: (user: ModdyUser | null) => void
    setToken: (token: string | null) => void
    setLoading: (loading: boolean) => void
    setError: (error: Error | null) => void
    setEjectionStatus: (status: string) => void
    clearEjectionStatus: () => void
    loadUserFromToken: (firebaseUser: FirebaseUser, decodedToken: DecodedToken) => Promise<ModdyUser>
    exchangeToken: (fbUser: FirebaseUser) => Promise<void>
    reset: () => void
}

export const useUserStore = create<UserState>((set, get) => ({
    user: null,
    firebaseUser: null,
    token: null,
    loading: true, // Start with loading true to prevent flash
    error: null,
    ejectionStatus: null,

    setFirebaseUser: (firebaseUser) => set({ firebaseUser }),
    setUser: (user) => set({ user }),
    setToken: (token) => set({ token }),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    setEjectionStatus: (status) => set({ ejectionStatus: status }),
    clearEjectionStatus: () => set({ ejectionStatus: null }),

    signIn: async (email: string, password: string) => {
        if (!isEmailAllowedForEnvironment(email)) {
            throw new Error(RESTRICTED_ENV_MESSAGE)
        }
        await signInWithEmailAndPassword(auth, email, password)
    },

    signInWithGoogle: async (): Promise<SignUpResult> => {
        tokenExchangeInProgress = true
        // Note: Don't set store's loading state here - it causes MarketingRoute to unmount the Signup component
        set({ error: null })
        const provider = new GoogleAuthProvider()

        try {
            // Step 1: Google OAuth popup
            const userCredential = await signInWithPopup(auth, provider)

            // Check environment restriction before proceeding
            if (!isEmailAllowedForEnvironment(userCredential.user.email || '')) {
                await signOut(auth)
                tokenExchangeInProgress = false
                return { success: false, error: 'unknown', message: RESTRICTED_ENV_MESSAGE }
            }

            set({ firebaseUser: userCredential.user })

            // Step 2: Exchange token (inline, not via auth listener)
            const idToken = await userCredential.user.getIdToken()
            const sessionId = getSessionId()

            const response = await fetch(`${API_URL}/auth/token`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionId,
                    userType: 'creator',
                    deviceInfo: { deviceId: getDeviceId() },
                }),
            })

            const data = await response.json()

            if (!response.ok) {
                if (data.error?.code === 'at_capacity') {
                    tokenExchangeInProgress = false
                    return {
                        success: false,
                        error: 'at_capacity',
                        message: data.error?.message || 'We are currently at capacity for new accounts.',
                    }
                }
                throw new Error(data.error?.message || data.message || 'Token exchange failed')
            }

            const decoded = jwtDecode(data.accessToken) as DecodedToken
            localStorage.setItem('accessToken', data.accessToken)
            localStorage.setItem('refreshToken', data.refreshToken)
            set({ token: data.accessToken })
            await get().loadUserFromToken(userCredential.user, decoded)
            tokenExchangeInProgress = false

            return { success: true }
        } catch (error) {
            // Keep tokenExchangeInProgress true to prevent auth listener from retrying
            set({ error: error as Error })

            if (error instanceof Error) {
                return { success: false, error: 'firebase', message: error.message }
            }

            return { success: false, error: 'unknown', message: 'An unexpected error occurred' }
        }
    },

    signOut: async () => {
        // Revoke session server-side before clearing local tokens
        try {
            const accessToken = localStorage.getItem('accessToken')
            if (accessToken) {
                const decoded = jwtDecode(accessToken) as DecodedToken
                if (decoded.sessionId) {
                    await fetch(`${API_URL}/auth/sessions/${decoded.sessionId}`, {
                        method: 'DELETE',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    })
                }
            }
        } catch {
            // Best-effort: don't block logout if revocation fails
        }

        clearSession()
        await signOut(auth)
    },

    reset: () => {
        clearSession()
        set({ user: null, firebaseUser: null, token: null })
    },

    signUp: async (email: string, password: string, profileData?: SignUpProfileData): Promise<SignUpResult> => {
        tokenExchangeInProgress = true
        // Note: Don't set store's loading state here - it causes MarketingRoute to unmount the Signup component
        set({ error: null })

        try {
            // Check environment restriction before creating account
            if (!isEmailAllowedForEnvironment(email)) {
                tokenExchangeInProgress = false
                return { success: false, error: 'unknown', message: RESTRICTED_ENV_MESSAGE }
            }

            // Step 1: Create Firebase account
            const userCredential = await createUserWithEmailAndPassword(auth, email, password)
            await sendEmailVerification(userCredential.user)
            set({ firebaseUser: userCredential.user })

            // Step 2: Exchange token (inline, not via auth listener)
            const idToken = await userCredential.user.getIdToken()
            const sessionId = getSessionId()

            const response = await fetch(`${API_URL}/auth/token`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionId,
                    userType: 'creator',
                    deviceInfo: { deviceId: getDeviceId() },
                    ...(profileData && { name: profileData.name, socials: profileData.socials }),
                }),
            })

            const data = await response.json()

            if (!response.ok) {
                if (data.error?.code === 'at_capacity') {
                    tokenExchangeInProgress = false
                    return {
                        success: false,
                        error: 'at_capacity',
                        message: data.error?.message || 'We are currently at capacity for new accounts.',
                    }
                }
                throw new Error(data.error?.message || data.message || 'Token exchange failed')
            }

            const decoded = jwtDecode(data.accessToken) as DecodedToken
            localStorage.setItem('accessToken', data.accessToken)
            localStorage.setItem('refreshToken', data.refreshToken)
            set({ token: data.accessToken })
            await get().loadUserFromToken(userCredential.user, decoded)
            tokenExchangeInProgress = false

            return { success: true }
        } catch (error) {
            // Keep tokenExchangeInProgress true to prevent auth listener from retrying
            set({ error: error as Error })

            if (error instanceof Error) {
                return { success: false, error: 'firebase', message: error.message }
            }

            return { success: false, error: 'unknown', message: 'An unexpected error occurred' }
        }
    },

    sendPasswordResetEmail: async (email: string) => {
        // Get the current domain for the continue URL
        const currentDomain = window.location.origin
        const actionCodeSettings = {
            url: `${currentDomain}/login`, // Where to redirect after password reset
            handleCodeInApp: true, // This must be true for custom reset page
        }

        await sendPasswordResetEmail(auth, email, actionCodeSettings)
    },

    loadUserFromToken: async (firebaseUser: FirebaseUser, decodedToken: DecodedToken) => {
        const newUser = {
            id: decodedToken.id,
            email: decodedToken.email || '',
            name: decodedToken.name || '',
            picture: decodedToken.picture || firebaseUser.photoURL || '',
            handle: decodedToken.handle || '',
            lastLogin: decodedToken.iat,
            roles: decodedToken.roles || [],
            status: decodedToken.status || '',
        }
        set({ user: newUser })
        return newUser
    },

    exchangeToken: async (fbUser: FirebaseUser) => {
        // Skip if signUp/signInWithGoogle is already handling the token exchange
        if (tokenExchangeInProgress) {
            return
        }

        try {
            // Keep loading true during entire exchange
            set({ loading: true, firebaseUser: fbUser })
            const accessToken = localStorage.getItem('accessToken')

            if (accessToken) {
                try {
                    const decoded = jwtDecode(accessToken) as DecodedToken
                    const now = Date.now() / 1000 // Convert to seconds

                    // Check if token is still valid (with 1 minute buffer)
                    if (decoded.exp && decoded.exp > now + 60) {
                        set({ token: accessToken })
                        await get().loadUserFromToken(fbUser, decoded)
                        // Only set loading false AFTER user is loaded
                        set({ loading: false })
                        return
                    }

                    // Token expired - clear tokens and fall through to re-exchange
                    // Don't call tokenService.clearTokens() here — preserve sessionId for re-exchange
                    clearSession()
                } catch (error) {
                    console.error('[UserStore] Invalid stored token:', error)
                    clearSession()
                }
            }

            const idToken = await fbUser.getIdToken()
            const sessionId = getSessionId()

            const response = await fetch(`${API_URL}/auth/token`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionId,
                    userType: 'creator',
                    deviceInfo: { deviceId: getDeviceId() },
                }),
            })

            const data = await response.json()

            // Handle capacity gating error
            if (!response.ok) {
                if (data.error?.code === 'at_capacity') {
                    throw new AtCapacityError(data.error?.message)
                }
                throw new Error(data.error?.message || data.message || 'Token exchange failed')
            }

            const decoded = jwtDecode(data.accessToken) as DecodedToken

            localStorage.setItem('accessToken', data.accessToken)
            localStorage.setItem('refreshToken', data.refreshToken)
            set({ token: data.accessToken })
            await get().loadUserFromToken(fbUser, decoded)
            // Only set loading false AFTER user is loaded
            set({ loading: false })
        } catch (error) {
            console.error('Token exchange failed:', error)
            // Clear all tokens and auth state so the user is redirected to login
            clearSession()
            try {
                await signOut(auth)
            } catch {
                // Best effort — clear store state regardless
            }
            set({ user: null, firebaseUser: null, token: null, error: error as Error, loading: false })
        }
    },

    getValidToken: async (forceRefresh: boolean = false) => {
        try {
            return await tokenService.refreshToken(forceRefresh)
        } catch (error) {
            console.log('Token refresh failed:', error)
            return null
        }
    },
}))

// Initialize auth state listener
let unsubscribe: (() => void) | null = null

export const initializeAuth = () => {
    if (unsubscribe) return // Already initialized

    const { setFirebaseUser, setLoading, exchangeToken } = useUserStore.getState()

    setLoading(true)

    // Validate stored tokens BEFORE Firebase auth restoration
    const accessToken = localStorage.getItem('accessToken')
    if (accessToken) {
        try {
            const decoded = jwtDecode(accessToken) as DecodedToken
            const now = Date.now() / 1000

            if (decoded.exp && decoded.exp <= now + 60) {
                console.log('[Auth] Stored access token expired, clearing...')
                // Don't call tokenService.clearTokens() — preserve sessionId for re-exchange
                clearSession(true)
            }
        } catch (error) {
            console.warn('[Auth] Invalid stored token, clearing...', error)
            clearSession()
        }
    }

    unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
        if (fbUser) {
            try {
                await fbUser.getIdToken(true)
            } catch (error) {
                console.error('Token refresh failed:', error)
            }
        }

        setFirebaseUser(fbUser)

        if (fbUser) {
            // Check if signup flow already set the user or token exchange is in progress
            const currentState = useUserStore.getState()
            if (currentState.user || tokenExchangeInProgress) {
                // Skip - signup flow already completed token exchange or is in progress
                return
            }

            await exchangeToken(fbUser)
        } else {
            setLoading(false)
        }
    })
}

export const cleanupAuth = () => {
    if (unsubscribe) {
        unsubscribe()
        unsubscribe = null
    }
}
